import * as React from "react";
import { useEffect, useRef } from "react";
import {
  useRive,
  useStateMachineInput,
  Layout,
  Fit,
  Alignment,
  type StateMachineInput,
  type Rive,
} from "@rive-app/react-canvas";

export interface RackRiveInputs {
  rive: Rive;
  getInput: (name: string) => StateMachineInput | null;
  listInputs: () => string[];
}

interface RackRiveProps {
  className?: string;
  stateMachine?: string;
  animation?: string;
  artboard?: string;
  autoplay?: boolean;
  fit?: Fit;
  alignment?: Alignment;
  scale?: number;
  onLoad?: (inputs: RackRiveInputs) => void;
}

export function RackRive({
  className,
  stateMachine = "State Machine 1",
  animation,
  artboard,
  autoplay = true,
  fit = Fit.Contain,
  alignment = Alignment.Center,
  scale = 1,
  onLoad,
}: RackRiveProps) {
  const hasCalledOnLoad = useRef(false);

  const { rive, RiveComponent } = useRive({
    src: "/rive/rack.riv",
    animations: animation,
    stateMachines: animation ? undefined : stateMachine,
    artboard,
    autoplay,
    layout: new Layout({ fit, alignment }),
  });

  useEffect(() => {
    if (rive && onLoad && !hasCalledOnLoad.current) {
      hasCalledOnLoad.current = true;
      onLoad({
        rive,
        getInput: (name: string) => {
          const inputs = rive.stateMachineInputs(stateMachine);
          return inputs?.find((input) => input.name === name) ?? null;
        },
        listInputs: () => {
          const inputs = rive.stateMachineInputs(stateMachine);
          return inputs?.map((input) => input.name) ?? [];
        },
      });
    }
  }, [rive, onLoad, stateMachine]);

  return (
    <div
      className={className}
      style={scale !== 1 ? { transform: `scale(${scale})` } : undefined}
    >
      <RiveComponent />
    </div>
  );
}

export { useStateMachineInput };
